import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin } from 'lucide-react';
import { companyInfo } from '../mock';
import { useTranslation } from 'react-i18next';

export const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="sm:col-span-2 lg:col-span-2">
            <Link to="/" className="inline-block mb-2">
              <img 
                src="/lamko-logo.png" 
                alt="LAMKO" 
                className="h-10 md:h-12 w-auto"
              />
            </Link>
            <p className="text-sm text-gray-300 mb-3">{t('footer.companyFullName')}</p>
            <p className="text-gray-400 mb-4 max-w-md text-sm md:text-base">
              At LAMKO, we aim to become a global tech- and R&D-led supplier of choice for specialty chemicals, with electronics-grade purification expertise and a CRDMO model purpose-built for semiconductor and display materials.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.linkedin.com/company/lamko"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-orange-500 transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-3 md:mb-4 text-sm md:text-base">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm md:text-base">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm md:text-base">
                  {t('nav.about')}
                </Link>
              </li>
              <li><Link to="/careers" className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm md:text-base">Careers</Link></li>
              <li>
                <Link to="/services" className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm md:text-base">
                  {t('nav.services')}
                </Link>
              </li>
              <li>
                <Link to="/capabilities" className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm md:text-base">
                  {t('nav.capabilities')}
                </Link>
              </li>
              <li>
                <Link to="/industries" className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm md:text-base">
                  {t('nav.industries')}
                </Link>
              </li>
              <li>
                <Link to="/brands" className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm md:text-base">
                  {t('nav.platformBrands')}
                </Link>
              </li>
              <li>
                <Link to="/partner" className="text-orange-400 hover:text-orange-300 font-semibold transition-colors duration-200 text-sm md:text-base" data-testid="footer-partner-link">
                  {t('nav.partnerWithUs')} →
                </Link>
              </li>
            </ul>
          </div>

                  {/* Explore */}
        <div>
          <h3 className="text-white font-semibold mb-3 md:mb-4 text-sm md:text-base">Explore</h3>
          <ul className="space-y-2">
            <li><Link to="/how-we-work" className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm md:text-base">How We Work</Link></li>
            <li><Link to="/quality" className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm md:text-base">Quality &amp; Compliance</Link></li>
            <li><Link to="/products" className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm md:text-base">Products &amp; Capabilities</Link></li>
            <li><Link to="/cda" className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm md:text-base">Confidentiality Agreement (CDA)</Link></li>
          </ul>
        </div>

        {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-3 md:mb-4 text-sm md:text-base">{t('footer.contact')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin size={16} className="text-orange-500 mt-1 flex-shrink-0" />
                <div>
                  <span className="text-gray-400 text-sm md:text-base block">{companyInfo.address}</span>
                  <span className="text-gray-400 text-sm md:text-base block">{companyInfo.addressKorean}</span>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail size={16} className="text-orange-500 mt-1 flex-shrink-0" />
                <a href={`mailto:${companyInfo.email}`} className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm md:text-base break-all">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Phone size={16} className="text-orange-500 mt-1 flex-shrink-0" />
                <span className="text-gray-400 text-sm md:text-base">{companyInfo.phone}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 md:mt-12 pt-6 md:pt-8 text-center text-gray-500 text-xs md:text-sm">
          <p className="mb-2">LAMKO Co., Ltd. | Business Reg. No. 574-86-03986 | Corporate Reg. No. 130111-0131223 | Rep. Dr. Yogesh Kisan Gawale</p><p>{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
};
